import { Intersection } from "../Intersection.js";
import { ORIENTATIONS } from "../../../constants/intxConstants";
import { errDiv, sumValues } from "../../../util/mathHelpers";

// Note: calculation in this class assumes all u-turn volumes follow the path of left turn
// as u/left turn volumes are combined in the web tool
export class RCUTUnsigIntersection extends Intersection {
  constructor(name, volumes, globalParams, majorStDirection) {
    super(name, volumes, globalParams);

    this.majorStDirection = majorStDirection;

    this.LaneConfig = {
      Z1: {
        Major2: { UT: 1 },
      },
      Z2: {
        Major1: { UT: 1 },
      },
      Z3: {
        Minor2: { RT: 1, RTChan: false },
        Major2: {
          LT: 1,
          T: 2,
          RT: 1,
          RTShared: false,
          RTChan: false,
        },
      },
      Z4: {
        Minor1: { RT: 1, RTChan: false },
        Major1: {
          LT: 1,
          T: 2,
          RT: 1,
          RTShared: false,
          RTChan: false,
        },
      },
    };

    this.volumes = this.generateDirectionalVolumes(this.majorStDirection);

    this.truckPcts =
      this.majorStDirection === ORIENTATIONS.EAST_WEST
        ? {
            Major1: Number(this.eastbound.truckDec),
            Major2: Number(this.westbound.truckDec),
            Minor1: Number(this.northbound.truckDec),
            Minor2: Number(this.southbound.truckDec),
          }
        : {
            Major1: Number(this.southbound.truckDec),
            Major2: Number(this.northbound.truckDec),
            Minor1: Number(this.eastbound.truckDec),
            Minor2: Number(this.westbound.truckDec),
          };
  }

  /**
   * Function to get the DEFAULT inputs available for the intersection.  This function is designed to facilitate the
   * integration of the engine into a user interface.
   *
   * A restricted crossing U-Turn (RCUT) has four zones (Z1-Z4) and is dependent on Major/Minor street designation.
   *
   * @return {Object} Object representation of default inputs
   */
  static getZoneDefaultInputs() {
    return {
      Z1: {
        Major2: { UT: 1 },
      },
      Z2: {
        Major1: { UT: 1 },
      },
      Z3: {
        Minor2: { RT: 1 },
        Major2: {
          LT: 1,
          T: 2,
          RT: 1,
          RTShared: false,
        },
      },
      Z4: {
        Minor1: { RT: 1 },
        Major1: {
          LT: 1,
          T: 2,
          RT: 1,
          RTShared: false,
        },
      },
    };
  }

  static getLaneConfigLabelsMapping(orientation) {
    const isEW = orientation === ORIENTATIONS.EAST_WEST;
    return {
      Z1: this.getIntersectionLabelsMappingWithMajorDir(isEW),
      Z2: this.getIntersectionLabelsMappingWithMajorDir(isEW),
      Z3: this.getIntersectionLabelsMappingWithMajorDir(isEW),
      Z4: this.getIntersectionLabelsMappingWithMajorDir(isEW),
    };
  }

  setLaneConfigInputs(laneConfigInputs) {
    this.LaneConfig = JSON.parse(JSON.stringify(laneConfigInputs));
  }

  // Implements the computeVCAnalysis function of the Intersection parent class.
  _runCriticalMovementAnalysis() {
    // Calaculate U Turn zone V/C results
    this.Z1CLV = sumValues(this.volumes.Major1);
    this.Z1VC = getUTurnZoneVc({
      utVolume: this.volumes.Minor2.T + this.volumes.Minor2.LT,
      conflictingVolume: this.Z1CLV,
      opposingLanes: this.LaneConfig.Z4.Major1.T,
      truckPct: this.truckPcts.Major2,
    });
    this.Z2CLV = sumValues(this.volumes.Major2);
    this.Z2VC = getUTurnZoneVc({
      utVolume: this.volumes.Minor1.T + this.volumes.Minor1.LT,
      conflictingVolume: this.Z2CLV,
      opposingLanes: this.LaneConfig.Z3.Major2.T,
      truckPct: this.truckPcts.Major1,
    });

    // Calculate main intersection V/C result
    // Zone 3: Major appraoch left turn
    this.Z3LtCLV =
      this.volumes.Major2.T +
      this.volumes.Major2.RT +
      this.volumes.Minor1.T +
      this.volumes.Minor1.LT;
    this.Z3LtVC = getMainIntxZoneLtCriticalVc({
      ltVolume: this.volumes.Major1.LT,
      conflictingVolume: this.Z3LtCLV,
      thruLanes: this.LaneConfig.Z3.Major2.T,
      truckPct: this.truckPcts.Major1,
    });
    // Zone 3: Minor approach
    this.Z3MinorStCLV =
      this.volumes.Major2.T +
      this.volumes.Minor1.LT +
      (this.LaneConfig.Z3.Major2.RTShared
        ? 0.5 * this.volumes.Major2.RT + 0.5 * this.volumes.Minor1.T
        : 0);
    this.Z3MinorStVC = getMainIntxZoneMinorCriticalVc({
      minorStVolume: sumValues(this.volumes.Minor2),
      conflictingVolume: this.Z3MinorStCLV,
      thruLanes: this.LaneConfig.Z3.Major2.T,
      truckPct: this.truckPcts.Minor2,
    });
    this.Z3VC = Math.max(this.Z3LtVC, this.Z3MinorStVC);
    this.Z3CLV = this.Z3VC === this.Z3LtVC ? this.Z3LtCLV : this.Z3MinorStCLV;

    // Zone 4: Major approach left turn
    this.Z4LtCLV =
      this.volumes.Major1.T +
      this.volumes.Major1.RT +
      this.volumes.Minor2.T +
      this.volumes.Minor2.LT;
    this.Z4LtVC = getMainIntxZoneLtCriticalVc({
      ltVolume: this.volumes.Major2.LT,
      conflictingVolume: this.Z4LtCLV,
      thruLanes: this.LaneConfig.Z4.Major1.T,
      truckPct: this.truckPcts.Major2,
    });
    // Zone 4: Minor approach
    this.Z4MinorStCLV =
      this.volumes.Major1.T +
      this.volumes.Minor2.LT +
      (this.LaneConfig.Z4.Major1.RTShared
        ? 0.5 * this.volumes.Major1.RT + 0.5 * this.volumes.Minor2.T
        : 0);
    this.Z4MinorStVC = getMainIntxZoneMinorCriticalVc({
      minorStVolume: sumValues(this.volumes.Minor1),
      conflictingVolume: this.Z4MinorStCLV,
      thruLanes: this.LaneConfig.Z4.Major1.T,
      truckPct: this.truckPcts.Minor1,
    });
    this.Z4VC = Math.max(this.Z4LtVC, this.Z4MinorStVC);
    this.Z4CLV = this.Z4VC === this.Z4LtVC ? this.Z4LtCLV : this.Z4MinorStCLV;

    this._resultsByZone = {
      Z1: {
        VC: this.Z1VC,
        CLV: this.Z1CLV,
      },
      Z2: {
        VC: this.Z2VC,
        CLV: this.Z2CLV,
      },
      Z3: {
        VC: this.Z3VC,
        CLV: this.Z3CLV,
      },
      Z4: {
        VC: this.Z4VC,
        CLV: this.Z4CLV,
      },
    };
  }
}

/**
 *
 * @param {number} utVolume sum of all volumes of on the minor street making the u turn
 * @param {number} conflictingVolume sum of all volumes of on the major street entering the zone
 * @param {number} utLanes number of U-turn lanes
 * @param {number} opposingLanes number of major street thru lanes
 * @param {number} truckPct truck percentage of the major approach that share lane with minor street u-turn vehicles
 * @returns {number} V/C ratio of the zone
 */
const getUTurnZoneVc = ({
  utVolume,
  conflictingVolume,
  opposingLanes,
  truckPct,
}) => {
  const tc_base = 4.4; // base critical headway
  const tc_hv = opposingLanes === 1 ? 1 : 2; // heavy vehicle critical headway
  const criticalHeadway = tc_base + tc_hv * truckPct;
  const tf_base = 2.6; // base follow up headway
  const tf_hv = opposingLanes === 1 ? 0.9 : 1; // heavy vehicle follow up headway
  const followupHeadway = tf_base + tf_hv * truckPct;
  const movementCapacity = getMovementCapacity({
    criticalHeadway,
    followupHeadway,
    conflictingVolume,
  });
  return errDiv(utVolume, movementCapacity);
};

/**
 * @param {number} ltVolume left turn volume of opposing major approach
 * @param {number} truckPct truck percentage of the left turn movement
 * @returns {number} V/C ratio of the zone
 */
const getMainIntxZoneLtCriticalVc = ({
  ltVolume,
  conflictingVolume,
  thruLanes,
  truckPct,
}) => {
  const tc_base = thruLanes === 3 ? 5.3 : 4.1; // base critical headway
  const tc_hv = thruLanes === 1 ? 1 : 2; // heavy vehicle critical headway
  const criticalHeadway = tc_base + tc_hv * truckPct;
  const tf_base = thruLanes === 3 ? 3.1 : 2.2; // base follow up headway
  const tf_hv = thruLanes === 1 ? 0.9 : 1; // heavy vehicle follow up headway
  const followupHeadway = tf_base + tf_hv * truckPct;
  const movementCapacity = getMovementCapacity({
    criticalHeadway,
    followupHeadway,
    conflictingVolume,
  });
  return errDiv(ltVolume, movementCapacity);
};

/**
 * @param {number} minorStVolume sum of volumes of minor approach
 * @param {number} truckPct truck percentage of the minor approach
 * @returns {number} V/C ratio of the zone
 */
const getMainIntxZoneMinorCriticalVc = ({
  minorStVolume,
  conflictingVolume,
  thruLanes,
  truckPct,
}) => {
  const tc_base = thruLanes === 1 ? 6.2 : thruLanes === 2 ? 6.9 : 7.1; // base critical headway
  const tc_hv = thruLanes === 1 ? 1 : 2; // heavy vehicle critical headway
  const criticalHeadway = tc_base + tc_hv * truckPct;
  const tf_base = thruLanes === 3 ? 3.9 : 3.3; // base follow up headway
  const tf_hv = thruLanes === 1 ? 0.9 : 1; // heavy vehicle follow up headway
  const followupHeadway = tf_base + tf_hv * truckPct;
  const movementCapacity = getMovementCapacity({
    criticalHeadway,
    followupHeadway,
    conflictingVolume,
  });
  return errDiv(minorStVolume, movementCapacity);
};

const getMovementCapacity = ({
  criticalHeadway,
  followupHeadway,
  conflictingVolume,
}) => {
  return (
    conflictingVolume *
    errDiv(
      Math.exp((-conflictingVolume * criticalHeadway) / 3600),
      1 - Math.exp((-conflictingVolume * followupHeadway) / 3600)
    )
  );
};
